import { useState, useEffect, useCallback, useRef } from 'react';
import { api, DfeLegacyImportEntry } from '@/lib/api';

interface UseImportHistoryReturn {
  imports: DfeLegacyImportEntry[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useImportHistory(): UseImportHistoryReturn {
  const [imports, setImports] = useState<DfeLegacyImportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchHistory = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getDfeLegacyImportStatus();
      if (fetchIdRef.current !== id) return;
      setImports(result.imports);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load import history');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { imports, loading, error, refresh };
}
